/// <amd-module name="Dashboards/DashboardView/src/redux/sagas/Saga" />
import { Dashboard as ServerDashboard, DashboardScope, Widget as ServerWidget } from "Dashboards/Common/Contracts/Dashboard";
import { TeamContext } from "Dashboards/Common/Contracts/TeamContext";
import { IDashboardsNotificationService, IDashboardsNotificationServiceName } from "Dashboards/Common/DashboardsNotificationService";
import * as DashboardRestClient from "Dashboards/Common/RestClient/Dashboard";
import { IDashboardsUserPermissionService } from "Dashboards/Common/UserPermissions";
import * as Resources from "Dashboards/DashboardView/Resources";
import * as DashboardPanelConstants from "Dashboards/DashboardPanel/Common";
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { TeamProject, WebApiTeam } from "Tfs/Platform/Contracts/Core";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { IProjectData, ITeamData } from "Tfs/Platform/Page";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataSelectors";
import { format } from "VSS/Core/Util/String";
import { IIdentity, IVssIdentityService, VssIdentityServiceName } from "VSS/Features/Identities/IdentityService";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLocationService } from "VSS/Platform/Location";
import { IMatchedContributedRoute, IVssNavigationService } from "VSS/Platform/Navigation";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { TelemetryAction, TelemetryActionTypes } from "VSS/Redux/Modules/Telemetry";
import { TTIMonitorActionTypes } from "VSS/Redux/Modules/TTIMonitor";
import { toggleFullScreen } from "VSSUI/HeaderCommandBar";
import { Dashboard } from "../../model/Dashboard";
import { PresentationMode } from "../../model/PresentationMode";
import { Widget } from "../../model/Widget";
import { copyDashboardFromServerData, copyWidgetsFromServerData } from "../../utilities/DashboardModelConverter";
import { loadPostTTILegacyDependencies } from "../../utilities/PostTTILoadedLegacyDependencies";
import { DashboardAction, DashboardActions, DashboardActionTypes } from "../actions/Actions";
import { ITeamProject, ITfsProjectService } from "TFS/Services/TfsProjectService";
import { ITfsTeamService } from "TFS/Services/TfsTeamService";
import { ErrorActions } from "../actions/ErrorActions";
import { OmittedTTIScenarioName, TelemetryActions } from "../actions/TelemetryActions";
import * as Selectors from "../selectors/Selectors";
import * as TelemetrySelectors from "../selectors/TelemetrySelectors";
import { customEffects, errorHandledSaga } from "./SagaHelpers";
import { FastPageSwitch } from "VSS/Platform/FPS";

/** Partial type of what's returned by DashboardContentProvider.cs */
interface IDashboardPluginSecuredObject<T> {
    value: T;
}

interface IDataProviderDashboard {
    Dashboard: ServerDashboard;
}

namespace DataProviderIds {
    export const currentDashboard = "ms.vss-dashboards-web.dashboards-content-provider";
    export const currentTeam = "ms.vss-dashboards-web.dashboards-team-data-provider";
    export const allTeams = "ms.vss-dashboards-web.dashboards-team-memberships-collection-provider";
}

export namespace DashboardViewSagas {
    /** The top-level saga to be included in a module and run by 'redux-saga' */
    export function* rootSaga(): SagaIterator {
        /**
         * The implementations of the sagas passed to effect.take* and effect.fork should be wrapped in
         * try/catch to prevent errors bubbling up to the root saga (use 'errorHandledSaga').
         *
         * Execution order of the sagas registered with effect.take* for the same pattern is unpredictable.
         * As such, do not use two of the same effect.take* calls for the same pattern to
         * run multiple sagas for a dispatched action. Prefer using effects.call/fork/race within a single
         * saga to control order.
         * Make sure when using two different effect.take* calls for the same pattern that the sagas
         * don't rely on executing in a particular order.
         */
        yield effects.takeLeading(DashboardActionTypes.ConfigureWidget, onLeadingConfigureWidgetAction);
        yield effects.takeLeading(DashboardActionTypes.CopyWidget, onLeadingCopyWidgetAction);
        yield effects.takeLeading(DashboardActionTypes.AddWidgetFromCatalog, onLeadingAddFromCatalogAction);
        yield effects.takeLeading(DashboardActionTypes.DropWidgetFromCatalog, onLeadingDropFromCatalogAction);
        yield effects.takeLeading(DashboardActionTypes.CopyWidgetToOtherDashboard, onLeadingCopyWidgetToOtherDashboardAction);
        yield effects.takeLeading(DashboardActionTypes.DeleteWidget, onLeadingDeleteWidgetAction);
        yield effects.takeLatest(DashboardActionTypes.ToggleFullScreen, onLatestToggleFullScreenAction);
        yield effects.takeLatest(DashboardActionTypes.RefreshDashboard, onLatestRefreshDashboardAction);
        yield effects.takeLatest(DashboardActionTypes.ActivateCopyDashboardPanel, activateCopyDashboardPanel);
        yield effects.takeLatest(DashboardActionTypes.NavigateToDashboard, onLatestNavigateToDashboard);
        yield effects.takeLatest(DashboardActionTypes.LoadNewTeamsForProject, onLatestLoadTeamsForProject);
        yield customEffects.takeOnce(DashboardActionTypes.NotifyDashboardInitialized, onFirstDashboardInitializedAction);
        yield effects.takeEvery(DashboardActionTypes.ToggleEditMode, onEveryToggleEditModeAction);
        yield effects.takeEvery(DashboardActionTypes.ToggleDashboardSettingsManager, onEveryToggleDashboardSettingsManagerAction);
        yield effects.takeEvery(DashboardActionTypes.NotifyWidgetsMoved, onEveryWidgetsMovedAction);
        yield effects.takeEvery(DashboardActionTypes.SavePreviewWidgetConfig, onEverySavePreviewWidgetConfigAction);
        yield effects.takeEvery(DashboardActionTypes.ToggleConfigMode, onToggleConfigMode);
        yield effects.takeEvery(DashboardActionTypes.UpdateDashboardSettings, onUpdateDashboardSettings);
        
        yield effects.fork(markInteractiveIfReady);
        yield effects.fork(loadPostTTILegacyScriptsIfNoLegacyWidgets);

        // Only take the leading so we don't try to perform multiple saves at once
        yield customEffects.takeLeadingAndLatest(DashboardActionTypes.SaveDashboard, onLeadingAndLatestSaveDashboardAction);

        try {
            yield effects.call(setOmitTenPercentWidgetRender);
            yield effects.call(loadPermissions);
            yield effects.call(setEmbeddedState);
            yield effects.call(loadCurrentDashboard);
            yield effects.call(loadTeamData);
            yield effects.call(loadOwnerData);
            yield effects.call(setMruDashboard);
            yield effects.call(publishDashboardComposition);
        } catch (e) {
            yield effects.put(ErrorActions.notifySagaFailure(e));
        }
    }

    const onLeadingConfigureWidgetAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.ConfigureWidget>
    ): SagaIterator {
        yield effects.call(configureWidget, action);
    });

    const onLeadingDeleteWidgetAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.DeleteWidget>
    ): SagaIterator {
        yield effects.call(deleteWidget, action);
    });

    const onLeadingCopyWidgetAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.CopyWidget>
    ): SagaIterator {
        yield effects.call(copyWidget, action);
    });

    const onLeadingAddFromCatalogAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.AddWidgetFromCatalog>
    ): SagaIterator {
        yield effects.call(addWidgetFromCatalog, action);
    });

    const onLeadingDropFromCatalogAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.DropWidgetFromCatalog>
    ): SagaIterator {
        yield effects.call(dropWidgetFromCatalog, action);
    });

    const onLeadingCopyWidgetToOtherDashboardAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.CopyWidgetToOtherDashboard>
    ): SagaIterator {
        yield effects.call(copyWidgetToOtherDashboard, action);
    });

    const onLatestToggleFullScreenAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.ToggleFullScreen>
    ): SagaIterator {
        yield effects.call(setFullScreen, action);
    });

    const onLatestRefreshDashboardAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.RefreshDashboard>
    ): SagaIterator {
        yield effects.put(DashboardActions.stopAutoRefreshTimer());
        yield effects.call(refreshCurrentDashboard, action.payload.isAutoRefresh, action.payload.isHardRefresh);
        yield effects.put(DashboardActions.startAutoRefreshTimer());
    });

    const onLatestNavigateToDashboard = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.NavigateToDashboard>
    ): SagaIterator {
        yield effects.call(navigateToDashboard, action.payload.dashboad);
    });

    const onLatestLoadTeamsForProject = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.LoadNewTeamsForProject>
    ): SagaIterator {
        yield effects.call(loadTeamsForProject, action.payload.project);
    });

    const onFirstDashboardInitializedAction = errorHandledSaga(function*(): SagaIterator {
        yield effects.put(DashboardActions.startAutoRefreshTimer());
    });

    const onEveryToggleEditModeAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.ToggleEditMode>
    ): SagaIterator {
        const startAutoRefreshTimer = !action.payload.showEditMode;
        yield effects.call(toggleAutoRefreshTimer, startAutoRefreshTimer);

        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dashboard: TelemetrySelectors.DashboardCompositionProperties = (yield effects.select(
            TelemetrySelectors.dashboardCompositionProperties,
            pageContext
        )) as any;
        
        const dashboardEditModeProps: TelemetrySelectors.IDashboardEditModeProperties = {
            dashboardId: dashboard.dashboardId,
            dashboardScope: dashboard.dashboardScope,
        }

        // Publish if user has clicked on Edit button
        if(action.payload.showEditMode){
            yield effects.put(TelemetryActions.publishOnDashboardEditMode(dashboardEditModeProps));
        }
        // Publish if user has clicked on 'Done Editing' button
        else{
            yield effects.put(TelemetryActions.publishOnDashboardDoneEditing(dashboardEditModeProps));
        }
    });

    const onEveryToggleDashboardSettingsManagerAction = errorHandledSaga(function*(
        action: ActionsOfType<DashboardAction, DashboardActionTypes.ToggleDashboardSettingsManager>
    ): SagaIterator {
        const startAutoRefreshTimer = !action.payload.showDashboardSettingsManager;
        yield effects.call(toggleAutoRefreshTimer, startAutoRefreshTimer);
    });

    const onEveryWidgetsMovedAction = errorHandledSaga(function*(): SagaIterator {
        yield effects.put(DashboardActions.saveDashboard());
    });

    const onEverySavePreviewWidgetConfigAction = errorHandledSaga(function*(): SagaIterator {
        yield effects.put(DashboardActions.saveDashboard());
        yield effects.call(restartTimer);
    });

    const onToggleConfigMode = errorHandledSaga(function*(): SagaIterator {
        yield effects.call(restartTimer);
    });

    const onUpdateDashboardSettings = errorHandledSaga(function*(): SagaIterator {
        yield effects.call(loadOwnerData);
    });

    function* restartTimer(): SagaIterator {
        const presentationMode: PresentationMode = (yield effects.select(Selectors.getPresentationMode)) as any;
        if (presentationMode == PresentationMode.View) {
            yield effects.call(toggleAutoRefreshTimer, true);
        }
    }

    const onLeadingAndLatestSaveDashboardAction = errorHandledSaga(function*(): SagaIterator {
        const inConfigMode: boolean = (yield effects.select(Selectors.showConfigMode)) as any;
        const widgetsChanged: boolean = (yield effects.select(Selectors.widgetsChanged)) as any;

        const canEdit: boolean = (yield effects.select(Selectors.canEditDashboard)) as any;
        const unpositionedWidgetWasPlaced = (yield effects.select(Selectors.unpositionedWidgetWasPlaced)) as any;

        // For the moment, the only time we need to save the dashboard is if the widgets changed outside of config mode.
        // Saves for dashboard changes like name/description are handled by the "manage settings" dialog, or widgets
        // at position (0, 0) were assigned a grid location.
        const shouldSaveDashboard: boolean = !inConfigMode && widgetsChanged && (canEdit || !unpositionedWidgetWasPlaced);

        // Only save if there is a change to save
        if (shouldSaveDashboard) {
            const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
            const client: DashboardRestClient.IDashboardRestClient = (yield effects.call(
                [pageContext, pageContext.getRestClient],
                "IDashboardRestClient"
            )) as any;
            const teamContext: TeamContext = (yield effects.select(Selectors.getDashboardTeamContext)) as any;

            const replacementDashboard: ServerDashboard = (yield effects.select(Selectors.packServerDashboard)) as any;
            let updatedDashboard: Dashboard | undefined;
            try {
                updatedDashboard = (yield effects.call(
                    [client, client.replaceDashboard],
                    replacementDashboard,
                    teamContext,
                    replacementDashboard.id
                )) as any;
            } catch (e) {
                yield effects.put(ErrorActions.notifyDashboardSaveFailure(e));
            }

            if (updatedDashboard != null) {
                const dashboard: Dashboard = (yield effects.call(copyDashboardFromServerData, updatedDashboard)) as any;
                const widgets: ReadonlyArray<Widget> = (yield effects.call(copyWidgetsFromServerData, updatedDashboard)) as any;

                yield effects.put(DashboardActions.setDashboard(dashboard));
                yield effects.put(DashboardActions.setPastWidgets(widgets));
                yield effects.put(DashboardActions.updateWidgetEtag(widgets));
            }
        }
    });

    const markInteractiveIfReady = errorHandledSaga(function*(): SagaIterator {
        yield effects.take(DashboardActionTypes.NotifyDashboardInitialized);

        // Wait until the dashboard has reached TTI
        let shouldMarkTTI: boolean = (yield effects.select(Selectors.shouldMarkTTI)) as any;
        while (!shouldMarkTTI) {
            yield effects.take(DashboardActionTypes.NotifyWidgetLoaded);
            shouldMarkTTI = (yield effects.select(Selectors.shouldMarkTTI)) as any;
        }

        const shouldOmitTTI: boolean = (yield effects.select(Selectors.shouldOmitTTI)) as any;
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const telemetry: TelemetrySelectors.DashboardRuntimeCompositionProperties = (yield effects.select(
            TelemetrySelectors.dashboardRuntimeCompositionProperties,
            pageContext
        )) as any;

        yield effects.call(publishCategorizedTTI, telemetry);

        yield shouldOmitTTI
            ? effects.put(TelemetryActions.markScenario(OmittedTTIScenarioName, telemetry, true /* isPageInteractive */))
            : effects.put(TelemetryActions.markTTI(telemetry));
    });

    const loadPostTTILegacyScriptsIfNoLegacyWidgets = errorHandledSaga(function*(): SagaIterator {
        const ttiEffect = effects.take(TTIMonitorActionTypes.MarkTTI);
        const dirtyTTIEffect = effects.take(
            (action: TelemetryAction) => action.type === TelemetryActionTypes.EndScenario && action.payload.scenarioName === OmittedTTIScenarioName
        );

        // Wait for TTI
        yield effects.race([ttiEffect, dirtyTTIEffect]);

        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dashboardHasLegacyWidgets: boolean = (yield effects.select(Selectors.dashboardHasLegacyWidgets, pageContext)) as any;

        // Only load legacy scripts if the dashboard didn't have any legacy widgets to already pre-load some legacy dependencies
        if (!dashboardHasLegacyWidgets) {
            // Delay retrieving the post-TTI scripts to avoid potentially slowing down any loading already
            // in progress for the remaining widgets of the dashboard.
            yield effects.delay(2000);
            yield effects.call(loadPostTTILegacyDependencies, pageContext);
        }
    });

    function* publishCategorizedTTI(telemetry: TelemetrySelectors.DashboardRuntimeCompositionProperties) {
        // @ts-expect-error TypeScript 4.2 upgrade
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        // @ts-expect-error TypeScript 4.2 upgrade
        const dashboardLoadCategory: string = (yield effects.select(Selectors.getBestLoadingStrategy, pageContext)) as any;

        yield effects.put(TelemetryActions.markScenario(dashboardLoadCategory, telemetry, true /* isPageInteractive */));
    }

    /**
     * Starts or stops the refresh timer depending on the value provided
     * @param value True if the timer should be started, False if the timer should be stopped
     */
    function* toggleAutoRefreshTimer(value: boolean): SagaIterator {
        if (value) {
            yield effects.put(DashboardActions.startAutoRefreshTimer());
        } else {
            yield effects.put(DashboardActions.stopAutoRefreshTimer());
        }
    }

    function* configureWidget(action: ActionsOfType<DashboardAction, DashboardActionTypes.ConfigureWidget>): SagaIterator {
        if (action.payload.widgetProps.id != null) {
            const telemetry: TelemetrySelectors.BasicWidgetProperties = (yield effects.select(
                TelemetrySelectors.configureWidgetProperties,
                action.payload.widgetProps
            )) as any;
            yield effects.put(TelemetryActions.publishOnConfigureWidget(telemetry));

            yield effects.put(DashboardActions.showConfigMode(action.payload.widgetProps.id));
            yield effects.call(toggleAutoRefreshTimer, false);
        } else {
            throw new Error("Widget Id must be specified to be configured");
        }
    }

    function* deleteWidget(action: ActionsOfType<DashboardAction, DashboardActionTypes.DeleteWidget>): SagaIterator {
        const telemetry: TelemetrySelectors.BasicWidgetProperties = (yield effects.select(
            TelemetrySelectors.deleteWidgetProperties,
            action.payload.widgetProps
        )) as any;
        yield effects.put(TelemetryActions.publishOnDeleteWidget(telemetry));
    }

    function* copyWidget(action: ActionsOfType<DashboardAction, DashboardActionTypes.CopyWidget>): SagaIterator {
        const copyProps = action.payload.widgetCopyProps;

        // Track usage of feature
        const telemetry: TelemetrySelectors.CopyToDashboardProperties = (yield effects.select(
            TelemetrySelectors.copyToDashboardProperties,
            copyProps
        )) as any;
        yield effects.put(TelemetryActions.publishOnCopyToDashboard(telemetry));

        const currentDashboard = (yield effects.select(Selectors.getDashboard)) as any;

        // Is the copy action targeting the current dashboard, or another?
        const isOnCurrentDashboard = currentDashboard.id == copyProps.destinationDashboard.id;

        // Handle the actual copy
        if (isOnCurrentDashboard) {
            yield effects.put(DashboardActions.copyWidgetToThisDashboard(copyProps.sourceWidget)); // Note - on-dashboard save is not tracked after local state is updated.
        } else {
            yield effects.put(DashboardActions.copyWidgetToOtherDashboard(copyProps));
        }

        // Request a self-dismissing acknowledgement of the copy, as a behavior outside the scope of the view.
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const messageService: IGlobalMessagesService = (yield effects.call([pageContext, pageContext.getService], "IGlobalMessagesService")) as any;
        yield effects.call([messageService, messageService.addToast], {
            duration: 3000,
            message: isOnCurrentDashboard
                ? format(Resources.AddToThisDashboardConfirmation, copyProps.sourceWidget.name)
                : format(Resources.AddToDashboardConfirmation, copyProps.sourceWidget.name, copyProps.destinationDashboard.name)
        });
    }

    function* addWidgetFromCatalog(action: ActionsOfType<DashboardAction, DashboardActionTypes.AddWidgetFromCatalog>): SagaIterator {
        const telemetry: TelemetrySelectors.BasicWidgetProperties = (yield effects.select(
            TelemetrySelectors.addFromCatalogProperties,
            action.payload.initialWidgetSettings
        )) as any;
        yield effects.put(TelemetryActions.publishOnAddFromCatalog(telemetry));

        yield effects.put(DashboardActions.copyWidgetToThisDashboard(action.payload.initialWidgetSettings)); // Note - on-dashboard save is not tracked after local state is updated.
    }

    function* dropWidgetFromCatalog(action: ActionsOfType<DashboardAction, DashboardActionTypes.DropWidgetFromCatalog>): SagaIterator {
        const telemetry: TelemetrySelectors.AddFromCatalogWidgetProperties = (yield effects.select(
            TelemetrySelectors.dropFromCatalogProperties,
            action.payload.initialWidgetSettings
        )) as any;

        yield effects.put(TelemetryActions.publishOnAddFromCatalog(telemetry));
    }

    // Note this implementation is intentionally not error-handled.
    function* copyWidgetToOtherDashboard(action: ActionsOfType<DashboardAction, DashboardActionTypes.CopyWidgetToOtherDashboard>): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const client: DashboardRestClient.IDashboardRestClient = (yield effects.call(
            [pageContext, pageContext.getRestClient],
            "IDashboardRestClient"
        )) as any;
        const projectData: IProjectData | undefined = (yield effects.select(currentProject)) as any;
        const teamContext: TeamContext = {
            projectId: projectData!.id,
            project: "",
            teamId: action.payload.widgetCopyProps.destinationDashboard.groupId || "",
            team: ""
        };
        const sourceWidget = action.payload.widgetCopyProps.sourceWidget;
        // Force Create Widget using limited set of creation params.
        const newWidget = {
            id: "",
            name: sourceWidget.name,
            size: sourceWidget.size,
            settings: sourceWidget.settings as string,
            settingsVersion: sourceWidget.settingsVersion,
            position: {
                column: 0,
                row: 0
            },
            contributionId: sourceWidget.contributionId
        } as ServerWidget;
        yield effects.call([client, client.createWidget], newWidget, teamContext, action.payload.widgetCopyProps.destinationDashboard.id);
    }

    function* setFullScreen(action: ActionsOfType<DashboardAction, DashboardActionTypes.ToggleFullScreen>): SagaIterator {
        // Request the framework show/hide page chrome
        yield effects.call(toggleFullScreen, action.payload.showFullScreen);

        // So grid detects the resize
        yield effects.call([window, window.dispatchEvent], new CustomEvent("resize"));
    }

    function* notifyDependentServicesOfPageRefresh(): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

        // Notify modern services of dashboard refresh
        const notificationService: IDashboardsNotificationService = (yield effects.call(
            [pageContext, pageContext.getService],
            IDashboardsNotificationServiceName
        )) as any;
        yield effects.call([notificationService, notificationService.notifyDashboardRefreshing]);

        // Notify legacy services of dashboard refresh
        const legacyService: ILegacyPlatformService = (yield effects.call([pageContext, pageContext.getService], "ILegacyPlatformService")) as any;
        const modules = (yield effects.call([legacyService, legacyService.requireModules], ["VSS/Events/Action"])) as any;
        const eventsActionService = (yield effects.call([modules[0], modules[0].getService])) as any;
        yield effects.call([eventsActionService, eventsActionService.performAction], "refreshtimer.on.refresh");
    }

    function* loadCurrentDashboard(): SagaIterator {
        try {
            const {
                value: { Dashboard: serverDashboard }
            }: IDashboardPluginSecuredObject<IDataProviderDashboard> = (yield effects.call(
                getDataProviderData,
                DataProviderIds.currentDashboard,
                Resources.Error_DashboardLoadFailed
            )) as any;
            const dashboard: Dashboard = (yield effects.call(copyDashboardFromServerData, serverDashboard)) as any;
            const widgets: ReadonlyArray<Widget> = (yield effects.call(copyWidgetsFromServerData, serverDashboard)) as any;

            yield effects.call(initializeDashboardAndWidgetState, dashboard, widgets);
            yield effects.call(setWindowTitle, dashboard);
        } catch (error) {
            throw new Error(`VS403716: ${error.message}`);
        }
    }

    /** Assigns the current dashboard as the last visited dashboard (and its owning team as the last visited team)
     *  to the settings service for experiences that consume MRU dashboard like "Continue where you left off" on the
     *  directory page and routing. */
    function* setMruDashboard(): SagaIterator {
        try {
            // We control the actions for setting dashboard and know this saga is called after they have been set.
            const dashboard: Dashboard | undefined = (yield effects.select(Selectors.getDashboard)) as any;

            // Because we assign our redux module dependencies before our own module in the array in RootProviderProps.ts,
            // we know the main sagas of the dependencies run before ours. So we don't need to wait for the
            // 'TfsPageData/Initialized' action to set the project data because it'll already have been dispatched.
            const projectData: IProjectData | undefined = (yield effects.select(currentProject)) as any;

            if (dashboard != null && projectData != null) {
                const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
                const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;

                const teamScopeEntries = { "Dashboard/LastVisited/DashboardId": dashboard.id };
                yield effects.call([settingsService, settingsService.setEntries], teamScopeEntries, SettingsUserScope.Me, "Project", projectData.id);

                if (dashboard.dashboardScope === DashboardScope.Project_Team) {
                    const projectScopeEntries = { "Dashboard/LastVisited/TeamId": dashboard.ownerId };
                    yield effects.call(
                        [settingsService, settingsService.setEntries],
                        projectScopeEntries,
                        SettingsUserScope.Me,
                        "Project",
                        projectData.id
                    );
                }
            }
        } catch (error) {
            throw new Error(`VS403719: ${error.message}`);
        }
    }

    /** Sets the document title using a formatted string and the name of the given dashboard. */
    function* setWindowTitle(dashboard: Dashboard): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const tfsNavService: ITfsNavigationService = (yield effects.call([pageContext, pageContext.getService], "ITfsNavigationService")) as any;
        const title = format(Resources.PageTitleFormat, dashboard.name);
        yield effects.call([tfsNavService, tfsNavService.setPageTitle], title);
    }

    function* refreshCurrentDashboard(isAutoRefresh: boolean, isHardRefresh: boolean): SagaIterator {
        // Track usage of feature
        const currentDashboard = (yield effects.select(Selectors.getDashboard)) as any;
        yield effects.put(TelemetryActions.publishOnRefresh(currentDashboard.id, isAutoRefresh, isHardRefresh));

        if (isHardRefresh) {
            window.location.reload();
            return;
        }

        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const client: DashboardRestClient.IDashboardRestClient = (yield effects.call(
            [pageContext, pageContext.getRestClient],
            "IDashboardRestClient"
        )) as any;
        const teamContext: TeamContext = (yield effects.select(Selectors.getDashboardTeamContext)) as any;

        let updatedDashboard: ServerDashboard | undefined;
        try {
            updatedDashboard = (yield effects.call([client, client.getDashboard], teamContext, currentDashboard.id)) as any;
        } catch (e) {
            yield effects.put(ErrorActions.notifyDashboardLoadFailure(e));
        }

        if (updatedDashboard != null) {
            yield effects.call(notifyDependentServicesOfPageRefresh);
            yield effects.put(DashboardActions.unloadDashboard());

            const dashboard: Dashboard = (yield effects.call(copyDashboardFromServerData, updatedDashboard)) as any;
            const widgets: ReadonlyArray<Widget> = (yield effects.call(copyWidgetsFromServerData, updatedDashboard)) as any;

            yield effects.call(initializeDashboardAndWidgetState, dashboard, widgets);
        }
    }

    function* initializeDashboardAndWidgetState(dashboard: Dashboard, widgets: ReadonlyArray<Widget>): SagaIterator {
        yield effects.put(DashboardActions.setDashboardAndWidgets(dashboard, widgets, widgets));
    }

    function* loadTeamData(): SagaIterator {
        try {
            const { value: team }: IDashboardPluginSecuredObject<ITeamData> = (yield effects.call(
                getDataProviderData,
                DataProviderIds.currentTeam,
                Resources.Error_DashboardTeamDataLoadFailed
            )) as any;
            yield effects.put(DashboardActions.setTeamData(team));

            if (!team) {
                yield effects.call(loadDefaultProjectTeamData);
            }
        } catch (error) {
            throw new Error(`VS403717: ${error.message}`);
        }
    }

    function* loadOwnerData(): SagaIterator {
        try {
            const dashboard: Dashboard | undefined = (yield effects.select(Selectors.getDashboard)) as any;

            // Load owner identity data.
            if (dashboard) {
                const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
                const pageService: IVssPageService = (yield effects.call([pageContext, pageContext.getService], "IVssPageService")) as any;
                const pageData: IPageData | undefined = (yield effects.call([pageService, pageService.getData])) as any;
                const identityService: IVssIdentityService = (yield effects.call(
                    [pageContext, pageContext.getService],
                    VssIdentityServiceName
                )) as any;
                const identityTypes = dashboard.dashboardScope === DashboardScope.Project_Team ? ["group"] : ["user"];
                const operationScope = pageData!.isHosted ? ["ims", "source"] : ["ims", "source", "ad"];
                const filterIdentities = "uid";
                const identities: IIdentity[] = (yield effects.call(
                    [identityService, identityService.searchIdentitiesAsync],
                    dashboard.ownerId,
                    identityTypes,
                    operationScope,
                    filterIdentities
                )) as any;

                if (identities) {
                    yield effects.put(DashboardActions.setOwnerIdentity(identities[0]));
                }
            }
        } catch (error) {
            throw new Error(`VS403718: ${error.message}`);
        }
    }

    function* loadDefaultProjectTeamData(): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const projectData: IProjectData | undefined = (yield effects.select(currentProject)) as any;
        const restClient: ICoreRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "ICoreRestClient")) as any;
        const teamProject: TeamProject = (yield effects.call([restClient, restClient.getProject], projectData!.id)) as any;
        yield effects.put(DashboardActions.setDefaultProjectTeamData(teamProject.defaultTeam));
    }

    function* loadPermissions(): SagaIterator {
        try {
            const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
            const permissionsService: IDashboardsUserPermissionService = (yield effects.call(
                [pageContext, pageContext.getService],
                "IDashboardsUserPermissionService"
            )) as any;
            const canCreateDashboards: boolean = (yield effects.call([permissionsService, permissionsService.CanCreateDashboards])) as any;
            const canEditDashboard: boolean = (yield effects.call([permissionsService, permissionsService.CanEditDashboards])) as any;
            const canManagePermissionsForDashboard: boolean = (yield effects.call([
                permissionsService,
                permissionsService.CanManagePermissionForDashboards
            ])) as any;

            yield effects.put(
                DashboardActions.setPermissions({
                    canCreateDashboards,
                    canEditDashboard,
                    canManagePermissionsForDashboard
                })
            );
        } catch (error) {
            throw new Error(`VS403714: ${error.message}`);
        }
    }

    function* setOmitTenPercentWidgetRender(): SagaIterator {
        try {
            const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
            const flagEnabled = (yield effects.call(
                isFeatureFlagEnabled,
                pageContext,
                "WebAccess.Dashboards.OmitTenPercentVisibleWidgetsFromRender.Enable",
                false
            )) as any;
            yield effects.put(DashboardActions.setOmitTenPercentVisibleWidgets(flagEnabled));
        } catch (error) {
            throw new Error(`VS403713: ${error.message}`);
        }
    }

    function* publishDashboardComposition(): SagaIterator {
        try {
            const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
            const telemetry: TelemetrySelectors.DashboardCompositionProperties = (yield effects.select(
                TelemetrySelectors.dashboardCompositionProperties,
                pageContext
            )) as any;

            yield effects.put(TelemetryActions.publishDashboardComposition(telemetry));
        } catch (error) {
            throw new Error(`VS403720: ${error.message}`);
        }
    }

    function* setEmbeddedState(): SagaIterator {
        try {
            const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
            const navService: IVssNavigationService = (yield effects.call([pageContext, pageContext.getService], "IVssNavigationService")) as any;
            const route: IMatchedContributedRoute = (yield effects.call([navService, navService.getCurrentRoute])) as any;
            const isEmbedded = route.routeId === "ms.vss-dashboards-web.dashboards-new-experience-route-embed";

            yield effects.put(DashboardActions.setEmbedded(isEmbedded));
        } catch (error) {
            throw new Error(`VS403715: ${error.message}`);
        }
    }

    /** Retrieves page data from a data provider.
     *  If the provider failed to return any data, the given error message is thrown with the data provider exception message. */
    function* getDataProviderData<T>(contributionId: string, errorMessage: string): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const contributionService: IVssContributionService = (yield effects.call(
            [pageContext, pageContext.getService],
            "IVssContributionService"
        )) as any;
        const data: T | undefined = (yield effects.call([contributionService, contributionService.getDataAsync], contributionId)) as any;

        if (!data) {
            const dataProviderExceptions: { [contributionId: string]: IServerException } = (yield effects.call([
                contributionService,
                contributionService.getDataProviderExceptions
            ])) as any;
            const ex = dataProviderExceptions[contributionId];
            const message = [errorMessage, ex && ex.message].join(" ");
            throw new Error(message);
        }

        return data;
    }

    /**
     * Saga that is executed when the Copy Dashboard button clicked
     */
    function* activateCopyDashboardPanel(): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

        yield effects.put(DashboardActions.showCopyDashboardPanel());
            
            const project: IProjectData = (yield effects.select(currentProject)) as any;
            
            let existingProjectList : DashboardPanelConstants.Project[] = (yield effects.select(Selectors.getCopyDashboardProjectsList)) as any;

            if (!existingProjectList || existingProjectList.length === 0)
                existingProjectList = (yield effects.call(getAllProjects, pageContext)) as any;
            
            let allTeamsForProject = (yield effects.call(getTeamsForProject, pageContext, project.id)) ?? [];

            const teamProjectMap :{[id:string]: DashboardPanelConstants.TeamProjectItem} = {};
            teamProjectMap[project.id] = { project: project, teamList: allTeamsForProject};

            const currentDashboard: ServerDashboard = (yield effects.select(Selectors.packServerDashboard)) as any;
            
            const copyDashboardProperties: TelemetrySelectors.CopyDashboardProperties = (yield effects.select(
                TelemetrySelectors.getCopyDashboardProperties,
                currentDashboard
            )) as any;

            yield effects.put(TelemetryActions.publishOnCopyDashboard(copyDashboardProperties));
            yield effects.put(DashboardActions.initializeStateForCopyDashboard(true, existingProjectList, teamProjectMap, currentDashboard));
    }

    function* navigateToDashboard(dashboard: Pick<TeamContext & Dashboard, "id" | "teamId" | "projectId" | "dashboardScope">): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

        const url: string = (yield effects.call(getDashboardUrl, pageContext, dashboard.projectId, dashboard.teamId, dashboard.id)) as any;

        const copyDashboardSuccessProperties: TelemetrySelectors.CopyDashboardSuccessProperties = (yield effects.select(
            TelemetrySelectors.getCopyDashboardSuccessProperties,
            { id: dashboard.id, dashboardScope: dashboard.dashboardScope }
        )) as any;

        yield effects.put(TelemetryActions.publishOnCopyDashboardSuccess(copyDashboardSuccessProperties));
        yield effects.call(FastPageSwitch, pageContext, url, true);
    }

    function* loadTeamsForProject(project: DashboardPanelConstants.Project): SagaIterator {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        let allTeamsForProject = (yield effects.call(getTeamsForProject, pageContext, project.id)) ?? [];
        const item: DashboardPanelConstants.TeamProjectItem = {project: project, teamList: allTeamsForProject }

        yield effects.put(DashboardActions.putNewTeamsForProject(project.id, item));

    }
    
    function* getAllProjects(pageContext: IVssPageContext) {
        const tfsProjectService: ITfsProjectService = (yield effects.call([pageContext, pageContext.getService], "ITfsProjectService"));
        const projects : ITeamProject[] = (yield effects.call([tfsProjectService, tfsProjectService.getAllProjectsLoadedPromise]));
        return projects.map(element => {
            return {
                id: element.id,
                name: element.name
            } as DashboardPanelConstants.Project
        });
    }

    function* getTeamsForProject(pageContext: IVssPageContext, projectId: string) {
        const tfsTeamService: ITfsTeamService = (yield effects.call([pageContext, pageContext.getService], "ITfsTeamService"));
        // getAllTeams only fetches the top 1000 teams for a project
        // Also fetch the teams the user is a part of in case they are missed by the above call
        const [allTeamsForProject, allTeamsForUser] = (yield effects.all([
            effects.call([tfsTeamService, tfsTeamService.getAllTeams], projectId),
            effects.call([tfsTeamService, tfsTeamService.getMyTeams], projectId)
        ])) as WebApiTeam[][];
        const allTeams = allTeamsForProject.concat(allTeamsForUser.filter( ({id}) => !allTeamsForProject.find(t => t.id == id) ));

        return allTeams.map(element => {
            return {
                id: element.id,
                name: element.name
            } as DashboardPanelConstants.Team
        });
    }

    function getDashboardUrl(
        pageContext: IVssPageContext,
        projectName: string,
        teamName: string | undefined,
        dashboardId: string
    ): string {
        const locationService: IVssLocationService = pageContext.getService("IVssLocationService");
    
        const routeValues: { [key: string]: string } = {
            project: projectName,
            id: dashboardId
        };
    
        if (teamName) {
            routeValues["team"] = teamName;
        }
    
        return locationService.routeUrl("ms.vss-dashboards-web.dashboards-new-experience-route", routeValues);
    }
}
